import { DT, MAX_FRAME_DELTA, MAX_STEPS } from '../sim/constants';

export interface LoopCallbacks {
  /** Un pas de simulation de durée DT exactement. */
  readonly update: () => void;
  /** Rendu. `alpha` ∈ [0,1) est la fraction de tick restant à interpoler. */
  readonly render: (alpha: number, frameMs: number) => void;
}

/** L'horloge et l'ordonnanceur, injectables.
 *
 *  Ce n'est pas de l'abstraction gratuite. L'accumulateur est la pièce du
 *  projet qui concentre le plus de modes de panne silencieux — spirale de la
 *  mort, alpha inversé, retard accumulé, jeu qui tourne 2,4× trop vite sur un
 *  144 Hz — et branché en dur sur `requestAnimationFrame`, il est intestable.
 *  Ce point d'injection permet de piloter le VRAI code de production, frame par
 *  frame, dans un test déterministe. */
export interface LoopHost {
  now(): number;
  schedule(cb: (t: number) => void): number;
  cancel(id: number): void;
}

const browserHost: LoopHost = {
  now: () => performance.now(),
  schedule: (cb) => requestAnimationFrame(cb),
  cancel: (id) => cancelAnimationFrame(id),
};

/**
 * La boucle. Pas fixe + accumulateur + interpolation (PLAN.md §3.4).
 *
 * Sans ce découplage, le jeu tourne 2,4× trop vite sur un écran 144 Hz :
 * `requestAnimationFrame` se cale sur le rafraîchissement natif, et la
 * spécification HTML ne décrit plus d'alignement exact sur le vsync — on ne
 * peut donc rien supposer de la cadence d'appel.
 *
 * Trois protections, chacune contre une panne précise :
 *   - CLAMP à MAX_FRAME_DELTA : au retour d'un onglet en arrière-plan, `delta`
 *     vaut plusieurs secondes. On le RAMÈNE à 250 ms — on ne l'annule pas : la
 *     simulation doit continuer d'avancer, simplement moins vite que le temps
 *     mural. L'annuler figerait le jeu à chaque hoquet dépassant le seuil.
 *   - MAX_STEPS : sur machine lente, on ABANDONNE le retard au lieu de
 *     l'accumuler, et l'accumulateur repart de ZÉRO. Le laisser chargé
 *     garantirait que la frame suivante sature à son tour, indéfiniment — soit
 *     exactement la spirale de la mort qu'on prétend éviter.
 *   - pas FIXE, jamais adaptatif : une machine lente rend moins de frames, elle
 *     ne simule pas moins. Sinon les replays divergent selon le matériel.
 */
export class Loop {
  private acc = 0;
  private prev = 0;
  private raf = 0;
  private running = false;
  private readonly cb: LoopCallbacks;
  private readonly host: LoopHost;

  /** Nombre de sous-pas exécutés à la dernière frame. Un 6 durable signale que
   *  la machine ne tient pas la cadence. */
  lastSteps = 0;

  /** Facteur de VITESSE DE JEU (accessibilité). Il multiplie le temps réel versé
   *  à l'accumulateur : < 1 = ralenti, > 1 = accéléré. La simulation reste à
   *  120 Hz EXACTEMENT — chaque sous-pas dure toujours `DT`, seul leur NOMBRE par
   *  frame change. Le déterminisme est intact : ralentir ne fait qu'espacer les
   *  ticks dans le temps réel, il n'en change aucun. */
  private speed = 1;

  constructor(cb: LoopCallbacks, host: LoopHost = browserHost) {
    this.cb = cb;
    this.host = host;
    this.frame = this.frame.bind(this);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.prev = this.host.now();
    this.acc = 0;
    this.raf = this.host.schedule(this.frame);
  }

  stop(): void {
    this.running = false;
    if (this.raf) this.host.cancel(this.raf);
    this.raf = 0;
  }

  /** À appeler au retour d'arrière-plan : repart d'une base propre au lieu de
   *  laisser `delta` absorber toute la durée de l'absence. */
  resync(): void {
    this.prev = this.host.now();
    this.acc = 0;
  }

  /** Règle la vitesse de jeu (accessibilité). Bornée à un intervalle raisonnable :
   *  trop lent rend une partie interminable, trop rapide la rend illisible. */
  setSpeed(s: number): void {
    this.speed = s < 0.5 ? 0.5 : s > 1.5 ? 1.5 : s;
  }

  private frame(now: number): void {
    if (!this.running) return;
    this.raf = this.host.schedule(this.frame);

    let delta = (now - this.prev) / 1000;
    this.prev = now;
    const frameMs = delta * 1000;

    // Un delta négatif ne devrait pas exister — mais `now` provient du
    // navigateur, et un ajustement d'horloge, une reprise de veille ou un
    // ordonnanceur de test peuvent en produire un. Sans cette garde,
    // l'accumulateur devient négatif : la simulation se fige jusqu'à ce qu'il
    // repasse au-dessus de DT, et alpha part hors de [0,1), ce qui fait
    // EXTRAPOLER l'interpolation au lieu d'interpoler. Une ligne contre une
    // panne qu'on ne reproduirait jamais volontairement.
    if (delta < 0) delta = 0;
    else if (delta > MAX_FRAME_DELTA) delta = MAX_FRAME_DELTA;
    // Vitesse de jeu appliquée ICI, après le clamp anti-veille : on scale le temps
    // versé à l'accumulateur, pas la durée d'un tick.
    this.acc += delta * this.speed;

    let steps = 0;
    while (this.acc >= DT && steps < MAX_STEPS) {
      this.cb.update();
      this.acc -= DT;
      steps++;
    }
    if (steps === MAX_STEPS) this.acc = 0;
    this.lastSteps = steps;

    // alpha = fraction du tick déjà écoulée mais pas encore simulée. Elle va de
    // 0 (on vient de simuler) vers 1 (le prochain tick est imminent), et
    // l'interpolation se lit `prev + (current - prev) * alpha`. L'inverser
    // ferait remonter le rendu vers la position d'AVANT le tick : tout
    // paraîtrait reculer d'une frame sur deux, avec un effet d'élastique très
    // visible mais difficile à diagnostiquer.
    this.cb.render(this.acc / DT, frameMs);
  }
}
